// src/pages/reports/LeepResponsesReport.js
import React, { useState, useEffect } from "react";
import { Container, Table, Row, Col, Button, Spinner } from "react-bootstrap";
import Select from "../../components/ReportSelect";
import axios from "axios";
import Pagination from "../../components/Pagination";
import "../../styles/Reports.css";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const QUESTIONS = [
  { value: "Q1", label: "Q1 - Aboriginal People" },
  { value: "Q2", label: "Q2 - Persons with Disabilities" },
  { value: "Q3", label: "Q3 - Visible Minorities" },
];

const ANSWERS = [
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
  { value: "all", label: "All" },
];

export default function LeepResponsesReport() {
  const navigate = useNavigate();

  // data
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);

  // dropdowns
  const [dropdowns, setDropdowns] = useState({
    categories: [],
    statuses: [],
  });

  // runtime
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // pending (UI controls)
  const [pQuestion, setPQuestion] = useState(null);
  const [pAnswer, setPAnswer] = useState(ANSWERS[2]); // "all"
  const [pQuery, setPQuery] = useState("");
  const [pCategory, setPCategory] = useState([]); // [{label, value}]
  const [pStatus, setPStatus] = useState([]); // [{label, value}]

  // frozen (applied filters)
  const [fQuestion, setFQuestion] = useState(null);
  const [fAnswer, setFAnswer] = useState("all");
  const [fQuery, setFQuery] = useState("");
  const [fCategory, setFCategory] = useState([]); // [ids as numbers]
  const [fStatus, setFStatus] = useState([]); // [ids as numbers]

  // fetch dropdowns (Applicant Type, Status)
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [cat, stat] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
        ]);
        setDropdowns({
          categories: Array.isArray(cat.data) ? cat.data : [],
          statuses: Array.isArray(stat.data) ? stat.data : [],
        });
      } catch (err) {
        console.error("Dropdown fetch failed", err);
        setDropdowns({ categories: [], statuses: [] });
      }
    };
    fetchDropdowns();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setHasSearched(true);

    const nextQuestion = pQuestion?.value || null;
    const nextAnswer = pAnswer?.value || "all";
    const nextQuery = (pQuery || "").trim();

    // freeze UI filters -> applied filters
    setFQuestion(nextQuestion);
    setFAnswer(nextAnswer);
    setFQuery(nextQuery);
    setFCategory(pCategory.map((c) => Number(c.value)));
    setFStatus(pStatus.map((s) => Number(s.value)));
    setCurrentPage(1);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // NOTE: category/status are NOT sent to backend unless you add support there.
      const [responsesRes, usersRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/reports/leep-responses`, {
          headers,
          params: {
            question: nextQuestion || undefined,
            answer: nextAnswer === "all" ? undefined : nextAnswer,
            search: nextQuery || undefined,
          },
        }),
        axios.get(`${BASE_URL}/api/users`, { headers }),
      ]);

      setData(Array.isArray(responsesRes.data) ? responsesRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (err) {
      console.error("LEEP report fetch error:", err);
      setData([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setPQuestion(null);
    setPAnswer(ANSWERS[2]); // "all"
    setPQuery("");
    setPCategory([]);
    setPStatus([]);

    setFQuestion(null);
    setFAnswer("all");
    setFQuery("");
    setFCategory([]);
    setFStatus([]);

    setData([]);
    setUsers([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  // build user map by id
  const userMap = {};
  users.forEach((u) => {
    userMap[u.id] = u;
  });

  // robust normalization helpers
  const toNumOrNull = (v) => (v == null || v === "" ? null : Number(v));

  // client filtering
  const filtered = data.filter((r) => {
    const user = userMap[r.employee_id] || {};

    const q = (fQuery || "").toLowerCase();
    const idStr = String(user.userid_no ?? "").toLowerCase();
    const nameStr = `${user.first_name || ""} ${
      user.last_name || ""
    }`.toLowerCase();

    const questionOK = !fQuestion || r.question === fQuestion;
    const answerOK = fAnswer === "all" || String(r.answer) === fAnswer;
    const searchOK = !fQuery || idStr.includes(q) || nameStr.includes(q);

    // Try multiple possible user keys and coerce to Number
    const userCategoryRaw =
      user.emp_category ?? user.category_id ?? user.emp_category_id ?? null;
    const userStatusRaw =
      user.status ?? user.status_id ?? user.emp_status_id ?? null;

    const userCategoryNum = toNumOrNull(userCategoryRaw);
    const userStatusNum = toNumOrNull(userStatusRaw);

    // Selected sets are numeric arrays
    const categoryOK =
      fCategory.length === 0 ||
      (userCategoryNum != null && fCategory.includes(userCategoryNum));

    const statusOK =
      fStatus.length === 0 ||
      (userStatusNum != null && fStatus.includes(userStatusNum));

    return questionOK && answerOK && searchOK && categoryOK && statusOK;
  });

  const paginatedData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const exportCSV = () => {
    if (filtered.length === 0) {
      alert("No data to export");
      return;
    }
    const headers = ["Employee ID", "Employee Name", "Question", "Answer"];
    const rows = filtered.map((r) => {
      const u = userMap[r.employee_id] || {};
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      const ans = r.answer === 1 ? "Yes" : "No";
      return [u.userid_no || "", name, r.question, ans]
        .map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`)
        .join(",");
    });
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, "LeepResponsesReport.csv");
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="fw-bold m-0">🧠 LEEP Responses</h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={4}>
          <label>Search (Employee ID or Name)</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search by ID or Name"
            value={pQuery}
            onChange={(e) => setPQuery(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <label>Applicant Type</label>
          <Select
            isMulti
            options={(dropdowns.categories || []).map((c) => ({
              label: c.c_name,
              value: c.c_id,
            }))}
            value={pCategory}
            onChange={setPCategory}
            classNamePrefix="rs"
            placeholder="All"
          />
        </Col>

        <Col md={2}>
          <label>Status</label>
          <Select
            isMulti
            options={(dropdowns.statuses || []).map((s) => ({
              label: s.s_TranslatedText,
              value: s.s_id,
            }))}
            value={pStatus}
            onChange={setPStatus}
            classNamePrefix="rs"
            placeholder="All"
          />
        </Col>

        <Col md={2}>
          <label>Question</label>
          <Select
            options={QUESTIONS}
            value={pQuestion}
            onChange={setPQuestion}
            classNamePrefix="rs"
            placeholder="All Questions"
            isClearable
          />
        </Col>

        <Col md={2}>
          <label>Answer</label>
          <Select
            options={ANSWERS}
            value={pAnswer}
            onChange={setPAnswer}
            classNamePrefix="rs"
          />
        </Col>
      </Row>
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto" className="align-end">
          <div className="button-group">
            <Button variant="primary" onClick={fetchData}>
              🔍 Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </Col>

        <Col md="auto" className="align-end ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || filtered.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="compact-table">
            <thead className="table-dark text-center">
              <tr>
                <th>Employee ID</th>
                <th>Employee Name</th>
                <th>Question</th>
                <th>Answer</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    No results found with current filters.
                  </td>
                </tr>
              ) : (
                paginatedData.map((r, idx) => {
                  const u = userMap[r.employee_id] || {};
                  return (
                    <tr key={`${r.question}-${idx}`}>
                      <td className="text-center">{u.userid_no || "—"}</td>
                      <td>
                        {`${u.first_name || ""} ${u.last_name || ""}`.trim()}
                      </td>
                      <td className="text-center">{r.question}</td>
                      <td className="text-center">
                        {r.answer === 1 ? "Yes" : "No"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
